import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useLanguage } from './LanguageContext';

const flagCodes = {
  de: 'de',
  en: 'gb',
};
const labels = {
  de: 'Deutsch',
  en: 'English',
};

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;
const FlagButton = styled.button<{ $active?: boolean }>`
  background: ${({$active}) => $active ? 'var(--primary-light)' : 'transparent'};
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  margin-left: 0.5rem;
  cursor: pointer;
  outline: ${({$active}) => $active ? '2px solid var(--primary)' : 'none'};
  transition: background 0.2s, outline 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  .fi {
    font-size: 1.3rem;
    margin: 0;
    display: block;
  }
  &:hover {
    background: var(--primary-light);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;
const DropdownMenu = styled.div`
  position: absolute;
  top: 110%;
  right: 0;
  background: var(--background-dark, #0f1525);
  border: 2px solid var(--primary);
  border-radius: 1rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  min-width: 180px;
  z-index: 100;
  padding: 0.6rem 0.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  
  @media (max-width: 768px) {
    min-width: 160px;
    right: -10px;
  }
`;

const DropdownItem = styled.button<{ $active?: boolean }>`
  background: ${({$active}) => $active ? 'var(--primary-light)' : 'transparent'};
  border: none;
  width: 100%;
  text-align: left;
  padding: 0.8rem 1.2rem 0.8rem 1rem;
  font-size: 1.1rem;
  color: ${({$active}) => $active ? '#111' : '#fff'};
  display: flex;
  align-items: center;
  gap: 0.7rem;
  border-radius: 0.6rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  font-weight: ${({$active}) => $active ? '600' : '400'};
  
  .fi {
    font-size: 1.3rem;
  }
  
  &:hover {
    background: var(--primary-light);
    color: #111;
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem 1rem 0.7rem 0.8rem;
    font-size: 1rem;
  }
`;

const LanguageDropdown: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = (lang: keyof typeof labels) => {
    setLanguage(lang);
    setOpen(false);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <DropdownWrapper ref={dropdownRef}>
      <FlagButton $active={open} onClick={() => setOpen((v) => !v)} aria-label="Sprache wählen">
        <span className={`fi fi-${flagCodes[language]}`} />
      </FlagButton>
      {open && (
        <DropdownMenu>
          {Object.keys(labels).map((lang) => (
            <DropdownItem
              key={lang}
              $active={language === lang}
              onClick={() => handleSelect(lang as keyof typeof labels)}
              aria-label={labels[lang as keyof typeof labels]}
            >
              <span className={`fi fi-${flagCodes[lang as keyof typeof labels]}`} />
              {labels[lang as keyof typeof labels]}
            </DropdownItem>
          ))}
        </DropdownMenu>
      )}
    </DropdownWrapper>
  );
};

export default LanguageDropdown;
